import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db8trhrk13ra8mig8uqg';
const FORM_ID = 'form-news-subscribe';

export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      formEl.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() !== '') payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '订阅失败，请稍后重试');
        return;
      }

      setStatus('success');
      formEl.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，订阅失败，请稍后重试');
    }
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-7">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/15 text-primary-500">
          <i className="ri-mail-send-line text-xl"></i>
        </span>
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">订阅影视资讯</h2>
          <p className="text-sm text-foreground-600">每周精选新片推荐与行业动态，直达你的邮箱。</p>
        </div>
      </div>

      <form
        id={FORM_ID}
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-5 flex flex-col gap-3 sm:flex-row"
      >
        <input
          type="email"
          name="email"
          required
          placeholder="请输入你的邮箱地址"
          aria-label="邮箱地址"
          className="h-12 flex-1 rounded-md border border-background-200/80 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
        />
        <select
          name="interest"
          aria-label="感兴趣的内容"
          defaultValue="新片推荐"
          className="h-12 rounded-md border border-background-200/80 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 sm:w-40"
        >
          <option value="新片推荐">新片推荐</option>
          <option value="影评">影评</option>
          <option value="行业动态">行业动态</option>
        </select>
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="subscribe-guard-field"
        />
        <button
          type="submit"
          disabled={status === 'submitting'}
          className={`flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 text-sm font-semibold text-background-50 transition-colors ${
            status === 'submitting' ? 'bg-primary-700' : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {status === 'submitting' ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50"></span>
              提交中
            </>
          ) : (
            <>
              立即订阅
              <i className="ri-arrow-right-line"></i>
            </>
          )}
        </button>
      </form>

      {status === 'success' ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-primary-500">
          <i className="ri-checkbox-circle-fill"></i>
          订阅成功！我们会通过邮箱为你推送精选影视资讯。
        </p>
      ) : null}

      {status === 'error' ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-accent-400">
          <i className="ri-error-warning-line"></i>
          {formError}
        </p>
      ) : null}

      <p className="mt-4 text-xs text-foreground-500">
        我们尊重你的隐私，订阅信息仅用于资讯推送，可随时取消。
      </p>
    </div>
  );
}